import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Ruler, TrendingUp, ImageOff } from "lucide-react";
import { getProductImages } from "@/lib/productImages";
import { uploadedImageUrl } from "@/components/ImageUploader";

export function formatPrice(p) {
  if (p == null) return null;
  return "€" + Number(p).toLocaleString("en-IE");
}

export function ProductImage({ src, alt, className = "" }) {
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    return (
      <div className={`flex flex-col items-center justify-center bg-brand-white text-muted-foreground ${className}`}>
        <ImageOff className="h-8 w-8 opacity-40" />
        <span className="mt-2 text-[11px] uppercase tracking-widest">Image coming soon</span>
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={`object-contain bg-white ${className}`} />;
}

export function ProductCard({ product, onEnquire, compareEnabled, isCompared, onToggleCompare }) {
  const price = formatPrice(product.price);
  const cardImage = (product.uploaded_images?.[0] && uploadedImageUrl(product.uploaded_images[0])) || product.image_url || getProductImages(product.slug)[0] || null;
  const contain = product.category === "domestic";
  return (
    <div
      className="group flex flex-col rounded-2xl border border-brand-navy/10 bg-white overflow-hidden transition-transform duration-200 hover:-translate-y-1 hover:border-brand-navy/25"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block relative">
        {contain || !cardImage ? (
          <ProductImage src={cardImage} alt={product.name} className="w-full h-52 p-4" />
        ) : (
          <img src={cardImage} alt={product.name} loading="lazy" className="w-full h-52 object-cover" />
        )}
        {product.brand && (
          <span className="absolute top-3 left-3 text-[11px] font-mono-spec uppercase tracking-wider px-2 py-1 rounded-full bg-brand-navy/10 text-brand-navy">
            {product.brand}
          </span>
        )}
      </Link>

      <div className="flex flex-col flex-1 p-5">
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-display font-semibold text-lg text-brand-ink leading-snug hover:text-brand-navy transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="mt-3 flex flex-wrap gap-2">
          {product.area_m2 != null && (
            <span className="inline-flex items-center gap-1 text-xs font-mono-spec px-2 py-1 rounded-md bg-brand-navy/10 text-brand-navy">
              <Ruler className="h-3 w-3" /> {product.area_m2.toLocaleString("en-IE")} m²
            </span>
          )}
          {product.max_slope != null && (
            <span className="inline-flex items-center gap-1 text-xs font-mono-spec px-2 py-1 rounded-md bg-brand-navy/10 text-brand-navy">
              <TrendingUp className="h-3 w-3" /> {product.max_slope}% slope
            </span>
          )}
        </div>

        <div className="mt-auto pt-4 flex items-center justify-between">
          {price ? (
            <span className="font-display font-bold text-xl text-brand-navy" data-testid={`product-price-${product.slug}`}>{price}</span>
          ) : (
            <span className="text-sm font-medium text-muted-foreground">Enquire for price</span>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <Button asChild variant="outline" className="flex-1 border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white">
            <Link to={`/product/${product.slug}`} data-testid={`product-view-${product.slug}`}>View</Link>
          </Button>
          <Button onClick={() => onEnquire(product)} className="flex-1 bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold" data-testid={`product-enquire-${product.slug}`}>
            Enquire
          </Button>
        </div>

        {compareEnabled && (
          <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <Checkbox checked={isCompared} onCheckedChange={() => onToggleCompare(product)} data-testid={`product-compare-${product.slug}`} />
            Compare
          </label>
        )}
      </div>
    </div>
  );
}
